import { useContext, useEffect } from 'react'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSort, faTh, faThLarge } from '@fortawesome/pro-light-svg-icons'

import { useSetState } from '@domain-group/react-hooks'

import {
  Button, DashboardModule, Link, ModalContext, SidebarModal, SidebarNoticeBox,
} from '@domain-group/suit-theme'

import { digObject } from '@domain-group/javascript-utils'

import useFlipperKey from '@hooks/useFlipperKey'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import useBulkSelectImages from './hooks/useBulkSelectImages'

import ImagesView from './components/ImagesView'

const defaultGalleryViewState = {
  sortOrder: 'newest' as 'newest' | 'oldest',
  viewMode: 'small' as 'small' | 'large',
}

const MODAL_KEY = 'BulkSelectImagesModal'

type ModalCallbacks = {
  closeModal: () => void,
  updateEntity: (payload: HandleCallbackActionParams) => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const BulkSelectImagesModal = (props: ModalProps) => {
  const { callbacks, showModal } = props
  const { closeModal, updateEntity } = callbacks || {}

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, MODAL_KEY, {})
  const {
    callbacks: { updateEntity: updateEntityFn }, entity,
  } = modalPayload

  const bulkSelectImagesPayload = useBulkSelectImages({ entity })
  const { sectionTitle } = bulkSelectImagesPayload

  const { isEnabled: fullScreen } = useFlipperKey('photos_plans_v2')

  const [galleryViewState, setGalleryViewState] = useSetState(defaultGalleryViewState)
  const { sortOrder, viewMode } = galleryViewState

  useEffect(() => {
    if (fullScreen){
      setGalleryViewState({ viewMode: 'large' })
    }
  }, [fullScreen])

  const {
    hasFilteredImages,
    hasSelectedEntities,
    imageIdsKey,
    updating,
    selectedEntityIds,
  } = bulkSelectImagesPayload

  const updateEntityPayload = {
    callbacks: {
      action: updateEntityFn,
      afterAction: closeModal,
    },
    entityParams: { [imageIdsKey]: selectedEntityIds },
    toastText: 'Images Updated',
  }

  return (
    <SidebarModal
      callbacks={callbacks}
      clickSafeZone
      modalKey={MODAL_KEY}
      showModal={showModal}
      size={fullScreen ? 'fullscreen' : undefined}
    >
      <SidebarModal.Header callbacks={callbacks} title="Select" titleSecondLine="Images" />

      <SidebarModal.Content>
        {fullScreen && (
          <DashboardModule
            headerRight={(
              <>
                <Link
                  greyLink={viewMode !== 'small'}
                  onClick={() => setGalleryViewState({ viewMode: 'small' })}
                  style={{ marginRight: 12 }}
                >
                  <FontAwesomeIcon icon={faTh} />
                </Link>

                <Link
                  greyLink={viewMode !== 'large'}
                  onClick={() => setGalleryViewState({ viewMode: 'large' })}
                  style={{ marginRight: 20 }}
                >
                  <FontAwesomeIcon icon={faThLarge} />
                </Link>

                <Link
                  onClick={() => setGalleryViewState({ sortOrder: sortOrder === 'newest' ? 'oldest' : 'newest' })}
                  textProps={{ fontSize: 'small' }}
                >
                  <FontAwesomeIcon icon={faSort} />{' '}
                  Sort : {sortOrder === 'newest' ? 'Newest to Oldest' : 'Oldest to Newest'}
                </Link>
              </>
            )}
            title={sectionTitle || 'Images'}
          />
        )}

        <SidebarNoticeBox
          boxProps={{
            marginBottom: 'large',
            ...(fullScreen ? { backgroundColor: 'backgroundColor' } : undefined),
          }}
        >
          <SidebarNoticeBox.Title>
            {hasFilteredImages
              ? 'Select Images in Order.'
              : 'No Images available'}

          </SidebarNoticeBox.Title>

          <SidebarNoticeBox.Paragraph>
            {hasFilteredImages
              ? 'You can select multiple images by clicking on them. The order that you select them will be the order that they are used.'
              : 'Please add images to the Photos and Plans tab.'}
          </SidebarNoticeBox.Paragraph>
        </SidebarNoticeBox>

        {hasFilteredImages && (
          <ImagesView
            bulkSelectImagesPayload={bulkSelectImagesPayload}
            fullScreen={!!fullScreen}
            setViewMode={(newViewMode: 'small' | 'large') => setGalleryViewState({ viewMode: newViewMode })}
            sortOrder={sortOrder}
            viewMode={viewMode}
          />
        )}
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!hasFilteredImages}
          loading={updating}
          onClick={() => updateEntity(updateEntityPayload)}
          size="large"
        >
          {hasSelectedEntities ? `Use Images (${selectedEntityIds.length})` : 'Select Images'}
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <BulkSelectImagesModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
